import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { datos } from '@/api/origen'
import { descargarArchivo } from '@/api/cliente'
import type {
  Departamento,
  DetallePeriodo,
  EmpresaNomina,
  Empleado,
  EstadoNomina,
  Grupo,
  Novedad,
  Obligacion,
  Parametro,
  Periodo,
  PeticionEmpleado,
  ResultadoLiquidacion,
  ResultadoSimulacion,
} from './tipos'

/**
 * Los datos de la nómina. Todo va contra `/interna/nomina`, que exige el
 * complemento encendido y el permiso del rol: si falta uno, el servidor dice
 * cuál y la pantalla lo enseña tal cual.
 */

export const clavesNomina = {
  todo: ['nomina'] as const,
  estado: ['nomina', 'estado'] as const,
  empresa: ['nomina', 'empresa'] as const,
  grupos: ['nomina', 'grupos'] as const,
  departamentos: ['nomina', 'departamentos'] as const,
  empleados: (salidos: boolean) => ['nomina', 'empleados', salidos] as const,
  empleado: (id: string) => ['nomina', 'empleado', id] as const,
  periodos: (grupo: string | undefined) => ['nomina', 'periodos', grupo ?? ''] as const,
  periodo: (id: string) => ['nomina', 'periodo', id] as const,
  novedades: (id: string) => ['nomina', 'periodo', id, 'novedades'] as const,
  parametros: ['nomina', 'parametros'] as const,
  calendario: (anio: number, mes: number) => ['nomina', 'calendario', anio, mes] as const,
}

/** Si el contribuyente tiene la nómina. Con un 403 (sin permiso) se da por no contratada. */
export function useEstadoNomina() {
  return useQuery({
    queryKey: clavesNomina.estado,
    queryFn: async ({ signal }) => {
      try {
        return await datos.obtener<EstadoNomina>('/nomina/estado', { senal: signal })
      } catch {
        return { contratada: false, configurada: false, paquete: null, limiteEmpleados: null } satisfies EstadoNomina
      }
    },
    staleTime: 300_000,
  })
}

export function useEmpresaNomina() {
  return useQuery({
    queryKey: clavesNomina.empresa,
    queryFn: ({ signal }) => datos.obtener<EmpresaNomina>('/nomina/empresa', { senal: signal }),
  })
}

export function useGrupos() {
  return useQuery({
    queryKey: clavesNomina.grupos,
    queryFn: ({ signal }) => datos.obtener<Grupo[]>('/nomina/grupos', { senal: signal }),
  })
}

export function useDepartamentos() {
  return useQuery({
    queryKey: clavesNomina.departamentos,
    queryFn: ({ signal }) => datos.obtener<Departamento[]>('/nomina/departamentos', { senal: signal }),
  })
}

export function useEmpleados(incluirSalidos: boolean) {
  return useQuery({
    queryKey: clavesNomina.empleados(incluirSalidos),
    queryFn: ({ signal }) =>
      datos.obtener<Empleado[]>('/nomina/empleados', { consulta: { incluirSalidos }, senal: signal }),
  })
}

export function useEmpleado(id: string | null) {
  return useQuery({
    queryKey: clavesNomina.empleado(id ?? ''),
    queryFn: ({ signal }) => datos.obtener<Empleado>(`/nomina/empleados/${encodeURIComponent(id ?? '')}`, { senal: signal }),
    enabled: id !== null,
    // La ficha trae el documento y la cuenta completos: no se guarda más de lo necesario.
    gcTime: 0,
  })
}

export function usePeriodos(grupo: string | undefined) {
  return useQuery({
    queryKey: clavesNomina.periodos(grupo),
    queryFn: ({ signal }) => datos.obtener<Periodo[]>('/nomina/periodos', { consulta: { grupo }, senal: signal }),
  })
}

export function useDetallePeriodo(id: string | null) {
  return useQuery({
    queryKey: clavesNomina.periodo(id ?? ''),
    queryFn: ({ signal }) => datos.obtener<DetallePeriodo>(`/nomina/periodos/${encodeURIComponent(id ?? '')}`, { senal: signal }),
    enabled: id !== null,
  })
}

export function useNovedades(id: string | null) {
  return useQuery({
    queryKey: clavesNomina.novedades(id ?? ''),
    queryFn: ({ signal }) =>
      datos.obtener<Novedad[]>(`/nomina/periodos/${encodeURIComponent(id ?? '')}/novedades`, { senal: signal }),
    enabled: id !== null,
  })
}

export function useParametros() {
  return useQuery({
    queryKey: clavesNomina.parametros,
    queryFn: ({ signal }) => datos.obtener<Parametro[]>('/nomina/parametros', { senal: signal }),
    staleTime: 3_600_000,
  })
}

export function useCalendario(anio: number, mes: number) {
  return useQuery({
    queryKey: clavesNomina.calendario(anio, mes),
    queryFn: ({ signal }) => datos.obtener<Obligacion[]>('/nomina/calendario', { consulta: { anio, mes }, senal: signal }),
    staleTime: 3_600_000,
  })
}

/** Una escritura que, al terminar, vuelve a pedir todo lo de la nómina. */
function useEscritura<V, R = unknown>(hacer: (v: V) => Promise<R>) {
  const cliente = useQueryClient()
  return useMutation({
    mutationFn: hacer,
    onSuccess: () => cliente.invalidateQueries({ queryKey: clavesNomina.todo }),
  })
}

export function useGuardarEmpresa() {
  return useEscritura((v: Partial<EmpresaNomina>) => datos.reemplazar<EmpresaNomina>('/nomina/empresa', { cuerpo: v }))
}

export function useCrearGrupo() {
  return useEscritura((v: { claveTss: number; nombre: string; frecuencia: string }) =>
    datos.crear<{ id: string }>('/nomina/grupos', { cuerpo: v }),
  )
}

export function useCrearDepartamento() {
  return useEscritura((v: { nombre: string; centroCosto?: string }) =>
    datos.crear<{ id: string }>('/nomina/departamentos', { cuerpo: v }),
  )
}

export function useCrearEmpleado() {
  return useEscritura((v: PeticionEmpleado) =>
    datos.crear<{ id: string; aviso: string | null }>('/nomina/empleados', { cuerpo: v }),
  )
}

export function useActualizarEmpleado(id: string) {
  return useEscritura((v: PeticionEmpleado) => datos.reemplazar(`/nomina/empleados/${encodeURIComponent(id)}`, { cuerpo: v }))
}

export function useRegistrarSalida(id: string) {
  return useEscritura((v: { fecha: string; causa: number }) =>
    datos.crear(`/nomina/empleados/${encodeURIComponent(id)}/salida`, { cuerpo: v }),
  )
}

export function useLiquidacion(id: string) {
  return useMutation({
    mutationFn: (v: {
      fechaSalida: string
      causa: number
      diasPreavisoOtorgados?: number
      diasVacacionesPendientes?: number
      vacacionesDelAnioDisfrutadas?: boolean
      fechaPago?: string
    }) => datos.crear<ResultadoLiquidacion>(`/nomina/empleados/${encodeURIComponent(id)}/liquidacion`, { cuerpo: v }),
  })
}

export function useSimulador() {
  return useMutation({
    mutationFn: (v: { salario: number; dependientes?: number }) =>
      datos.crear<ResultadoSimulacion>('/nomina/simulador', { cuerpo: v }),
  })
}

export type AccionPeriodo = 'calcular' | 'aprobar' | 'desaprobar' | 'cerrar' | 'pagar'

export function useAccionPeriodo(id: string) {
  return useEscritura((accion: AccionPeriodo) =>
    datos.crear<{ avisoPaquete?: string | null } | undefined>(`/nomina/periodos/${encodeURIComponent(id)}/${accion}`),
  )
}

export function useAgregarNovedad(periodo: string) {
  return useEscritura(
    (v: { empleadoId: string; clase: 'ingreso' | 'descuento'; concepto: number; monto: number; descripcion?: string }) =>
      datos.crear(`/nomina/periodos/${encodeURIComponent(periodo)}/novedades`, { cuerpo: v }),
  )
}

export function useBorrarNovedad(periodo: string) {
  return useEscritura((novedad: string) =>
    datos.eliminar(`/nomina/periodos/${encodeURIComponent(periodo)}/novedades/${encodeURIComponent(novedad)}`),
  )
}

export function descargarArchivoTss(anio: number, mes: number, proceso: 'AM' | 'AR' | 'NV'): Promise<void> {
  return descargarArchivo('/nomina/archivos/tss', { anio, mes, proceso })
}

export function descargarPagoCsv(periodo: string): Promise<void> {
  return descargarArchivo(`/nomina/periodos/${encodeURIComponent(periodo)}/pago.csv`)
}
